import React, { useState, useMemo } from 'react';
import { generarDocumentoBitacora } from '../../../services/vehiculosService';
import revisionService from '../../../services/revisionService';
import './bitacora-veh.css';

const BitacoraVeh = ({ vehiculo, registros = [], showToast }) => {
  const [filtro,    setFiltro]    = useState('todos');
  const [loadingId, setLoadingId] = useState(null);

  const filtrados = useMemo(() => {
    const base = filtro === 'todos' 
      ? registros 
      : registros.filter(r => getCategoria(r) === filtro);
    return [...base].sort((a, b) => new Date(getFechaOrden(b)) - new Date(getFechaOrden(a)));
  }, [registros, filtro]);

  const grupos = useMemo(() => agruparPorMes(filtrados), [filtrados]);

  const verDocumento = async (reg) => {

    // Los taller_nuevo solo tienen documento una vez entregados
    if (!tieneDocumento(reg)) return;
 
    setLoadingId(getKey(reg));

    // Abrir nueva ventana de forma síncrona, dentro del gesto del click (para evitar bloqueos del navegador iOS)
    const ventana = window.open('', '_blank');

    try {
      // Obtener el  PDF como blob (se genera o se descarga del servidor según el tipo de registro)
      const blob = await getBlobDocumento(reg);
      const url  = URL.createObjectURL(blob);
      
      // Asignarlo a la ventana ya abierta
      if (ventana) {
        ventana.location.href = url;
      } else {
        // Si el navegador igual bloqueó el popup (usuario deshabilitó popups)
        showToast({ severity: 'warn', summary: 'Popup bloqueado', detail: 'Habilita las ventanas emergentes para ver el documento', life: 4000 });
      }

    } catch (error)  {
      console.error(error);
      if (ventana) ventana.close();
      showToast({ 
        severity: 'error',  
        summary: 'Error', 
        detail: typeof error === 'string' ? error : 'No se pudo obtener el documento',
        life: 3500 
      });
    } finally {
      setLoadingId(null);
    }
  };

  if (!vehiculo) {
    return (
      <div className='p-1'>
        <div className="alert alert-primary" role="alert">
          <h5 className="alert-heading">Histórico del vehículo</h5>
          <p>
            En este apartado podrá encontrar la línea de tiempo con los eventos
            cronológicos asociados al vehículo seleccionado.
          </p>

          <hr></hr>

          <p>
            Aquí se registran distintos eventos relacionados con la operación,
            mantenimiento y seguimiento del vehículo.
          </p>
          
          <p>
            Dentro de la línea de tiempo podrá visualizar:
          </p>
          
          <ul className="pl-4">
            <li>Archivos de los informes generados por el taller mecánico.</li>
            <li>Archivos de las revisiones realizadas por conductores.</li>
            <li>Observaciones dejadas por conductores y mecánicos en sus respectivos informes.</li>
          </ul>

          <hr></hr>
          
          <p>
            Seleccione un vehículo para visualizar su historial y eventos asociados.
          </p>
          
        </div>
      </div>
    );
  }

  return (
    <div className="bt-wrap">
      <div className="bt-header">
        <div className="bt-title-row">
          <div className="bt-hicon">
            <i className="pi pi-book" />
          </div>
          <div>
            <p className="bt-title">Histórico del vehículo</p>
            <p className="bt-subtitle">{vehiculo.patente} · {vehiculo.marca} {vehiculo.modelo}</p>
          </div>
        </div>

        <div className="bt-filters">
          {[
            { key: 'todos',     label: 'Todos'     },
            { key: 'taller',    label: 'Taller'    },
            { key: 'conductor', label: 'Conductor' },
          ].map(f => (
            <button
              key={f.key}
              className={`bt-filter ${filtro === f.key ? `active-${f.key}` : ''}`}
              onClick={() => setFiltro(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtrados.length === 0 && (
        <div className="bt-empty">
          <i className="pi pi-inbox" />
          <p>Sin registros{filtro !== 'todos' ? ` de tipo ${filtro}` : ''}</p>
        </div>
      )}

      {filtrados.length > 0 && (
        <div className="bt-timeline">
          {Object.entries(grupos).map(([mes, items]) => (
            <div key={mes}>
              <p className="bt-group-label">{mes}</p>
              {items.map((reg) => {
                const key = getKey(reg);
                const cardProps = {
                  reg,
                  cargando: loadingId === key,
                  docDisponible: tieneDocumento(reg),
                  onVerDoc: () => verDocumento(reg),
                };
                return (
                  <div key={key} className="bt-item">

                    <div className={`bt-dot ${getCategoria(reg)}`} />
                    {reg.tipo === 'taller_nuevo'   && <CardTallerNuevo   {...cardProps} />}
                    {reg.tipo === 'taller_antiguo' && <CardTallerAntiguo {...cardProps} />}
                    {reg.tipo === 'conductor'      && <CardConductor     {...cardProps} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BitacoraVeh;



const ESTADO_CHECKLIST = { 
  1: 'OPERATIVO',
  2: 'MANTENIMIENTO',
  3: 'ALERTA_CRÍTICA',
  4: 'ALERTA_MEDIA',
  5: 'DESCONOCIDO'
};

const TIPO_TRABAJADOR = {
  tecnico:  'Técnico',
  mecanico: 'Mecánico',
};

// 'taller_antiguo' | 'taller_nuevo' -> 'taller' ; 'conductor' -> 'conductor'
const getCategoria = (reg) => reg.tipo?.startsWith('taller') ? 'taller' : reg.tipo;

const getFechaOrden = (reg) => {
  switch (reg.tipo) {
    case 'taller_nuevo':   return reg.fecha_ingreso;
    case 'taller_antiguo': return reg.fechaIngreso;
    default:               return reg.creado_en;
  }
};

// conductor y taller_antiguo: el PDF se genera al vuelo, siempre disponible.
// taller_nuevo: el PDF oficial solo existe una vez entregado (documento ya generado y guardado).
const tieneDocumento = (reg) => {
  if (reg.tipo !== 'taller_nuevo') return true;
  return reg.estado_recepcion?.toLowerCase() === 'entregado';
};

// Devuelve siempre una Promise<Blob>, sin importar el origen del documento
const getBlobDocumento = (reg) => {
  switch (reg.tipo) {
    case 'taller_nuevo':
      // PDF oficial ya generado: el backend lo busca por id de revisión
      return revisionService.verPdfOficial(reg.id_revision);
 
    case 'taller_antiguo':
      return generarDocumentoBitacora('taller', reg.numeroOT)
        .then(pdf => new Promise(resolve => pdf.getBlob(resolve)));
 
    default: // conductor
      return generarDocumentoBitacora(reg.tipo, reg.id_checklist)
        .then(pdf => new Promise(resolve => pdf.getBlob(resolve)));
  }
};

// Key única para React / estado de carga (evita choques entre id_revision de tablas distintas)
const getKey = (reg) => {
  const id = reg.tipo === 'conductor' ? reg.id_checklist : reg.id_revision;
  return `${reg.tipo}-${id}`;
};

// La BD devuelve 1969-12-31 (epoch 0) cuando la fecha de salida es nula
const fechaValida = (str) => {
  if (!str) return false;
  const d = new Date(str);
  return !isNaN(d) && d.getFullYear() > 1970;
};

const agruparPorMes = (registros) =>
  registros.reduce((acc, reg) => {
    const d   = new Date(getFechaOrden(reg));
    const key = d.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    if (!acc[key]) acc[key] = [];
    acc[key].push(reg);
    return acc;
  }, {});

const formatFecha = (str) => new Date(str).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });

const formatFechaHora = (str) => {
  const d = new Date(str);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
};

const iniciales = (nombre = '') => nombre.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();

// "en_proceso" -> "En proceso"
const formatEstado = (str = '') => {
  const t = str.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

//  Sub componentes

// Tarjeta para registros anteriores 
// (formato antiguo)
function CardTallerAntiguo({ reg, onVerDoc, cargando }) {
  return (
    <div className="bt-card">
      <div className="bt-card-top">
        <div className="bt-card-top-left">
          <span className="bt-pill taller">Taller</span>
          <span className="bt-ot">{reg.numeroOT}</span>
          {reg.servicio_tipo && (
            <span className="bt-servicio-tipo">{reg.servicio_tipo}</span>
          )}
        </div>
        <span className="bt-time">{formatFecha(reg.fechaIngreso)}</span>
      </div>
 
      <div className="bt-divider" />
 
      <div className="bt-card-body">
        <div className="bt-grid">
          <div className="bt-field">
            <span className="bt-label">Mecánico(s)</span>
            <div className="bt-mechs">
              <div className="bt-avatar bt-avatar--taller">{iniciales(reg.mecanico_nombre)}</div>
              <span className="bt-mech-name">{reg.mecanico_nombre}</span>
              {reg.mecanico2_nombre && (
                <>
                  <span className="bt-sep">·</span>
                  <div className="bt-avatar bt-avatar--taller">{iniciales(reg.mecanico2_nombre)}</div>
                  <span className="bt-mech-name">{reg.mecanico2_nombre}</span>
                </>
              )}
            </div>
          </div>
          <div className="bt-field">
            <span className="bt-label">Ingreso / Salida</span>
            <span className="bt-value">
              {formatFecha(reg.fechaIngreso)}
              {fechaValida(reg.fechaSalida) ? ` → ${formatFecha(reg.fechaSalida)}` : ' → En taller'}
            </span>
          </div>
        </div>
 
        {reg.diagnostico && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Diagnóstico</span>
            <span className="bt-value">{reg.diagnostico}</span>
          </div>
        )}
 
        {reg.obsMecanico && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Observación mecánico</span>
            <span className="bt-value bt-value--muted">{reg.obsMecanico}</span>
          </div>
        )}
 
        {reg.recomendacion && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Recomendación</span>
            <span className="bt-value bt-value--muted">{reg.recomendacion}</span>
          </div>
        )}
      </div>

      <FooterDoc onVerDoc={onVerDoc} cargando={cargando} />
    </div>
  );
}

// Tarjeta para registros nuevos 
// (recepción con estados, centro de costo, trabajadores y servicios)
function CardTallerNuevo({ reg, onVerDoc, cargando, docDisponible }) {
  const trabajadores = reg.trabajadores ?? [];
  
  // Los servicios pueden venir repetidos, se muestran una sola vez
  const servicios = [...new Set((reg.servicios ?? []).map(s => s.servicio).filter(Boolean))];

  const tieneSalida = fechaValida(reg.fecha_salida);
 
  return (
    <div className="bt-card bt-card--nuevo">
      <div className="bt-card-top">
        <div className="bt-card-top-left">
          <span className="bt-pill taller">Taller</span>
          <span className="bt-ot">{reg.numero_ot}</span>
          {reg.codigo_centrocosto && (
            <span className="bt-cc">
              {reg.codigo_centrocosto}
              {reg.nombre_centrocosto ? ` · ${reg.nombre_centrocosto}` : ''}
            </span>
          )}
        </div>
        <span className="bt-time">{formatFecha(reg.fecha_ingreso)}</span>
      </div>
 
      {/* Estados del flujo */}
      <div className="bt-estados">
        {reg.estado_recepcion && (
          <span className={`bt-badge bt-badge--${reg.estado_recepcion}`}>
            <span className="bt-badge-key">Recepción</span> {formatEstado(reg.estado_recepcion)}
          </span>
        )}
        {reg.estado_taller && (
          <span className={`bt-badge bt-badge--${reg.estado_taller}`}>
            <span className="bt-badge-key">Taller</span> {formatEstado(reg.estado_taller)}
          </span>
        )}
        {reg.estado_firmas && (
          <span className={`bt-badge bt-badge--${reg.estado_firmas}`}>
            <span className="bt-badge-key">Firmas</span> {formatEstado(reg.estado_firmas)}
          </span>
        )}
      </div>
 
      <div className="bt-divider" />
 
      <div className="bt-card-body">
        <div className="bt-grid">
          <div className="bt-field">
            <span className="bt-label">Equipo de trabajo</span>
            {trabajadores.length > 0 ? (
              <div className="bt-team">
                {trabajadores.map((t, i) => (
                  <div key={`${t.nombre}-${i}`} className="bt-mechs">
                    <div className="bt-avatar bt-avatar--taller">{iniciales(t.nombre)}</div>
                    <span className="bt-mech-name">{t.nombre}</span>
                    <span className="bt-role">{TIPO_TRABAJADOR[t.tipo] ?? t.tipo}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="bt-value bt-value--muted">Sin asignar</span>
            )}
          </div>
 
          <div className="bt-field">
            <span className="bt-label">Ingreso / Salida</span>
            <span className="bt-value">
              {formatFecha(reg.fecha_ingreso)}
              {tieneSalida ? ` → ${formatFecha(reg.fecha_salida)}` : ' → En taller'}
            </span>
            {reg.kilometraje_act != null && (
              <span className="bt-value bt-value--muted">
                {Number(reg.kilometraje_act).toLocaleString('es-CL')} km
              </span>
            )}
          </div>
        </div>
 
        {servicios.length > 0 && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Servicios</span>
            <div className="bt-chips">
              {servicios.map(s => (
                <span key={s} className="bt-servicio-tipo">{s}</span>
              ))}
            </div>
          </div>
        )}
 
        {reg.motivo_ingreso && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Motivo de ingreso</span>
            <span className="bt-value">{reg.motivo_ingreso}</span>
          </div>
        )}
 
        {reg.diagnostico && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Diagnóstico</span>
            <span className="bt-value">{reg.diagnostico}</span>
          </div>
        )}
 
        {reg.recomendado && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Trabajo recomendado</span>
            <span className="bt-value bt-value--muted">{reg.recomendado}</span>
          </div>
        )}
 
        {reg.realizado && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Trabajo realizado</span>
            <span className="bt-value">{reg.realizado}</span>
          </div>
        )}
 
        {reg.obs_mecanico && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Observación mecánico</span>
            <span className="bt-value bt-value--muted">{reg.obs_mecanico}</span>
          </div>
        )}
      </div>
 
      <FooterDoc onVerDoc={onVerDoc} cargando={cargando} docDisponible={docDisponible} />
    </div>
  );
}

function CardConductor({ reg, onVerDoc, cargando }) {
  const estado = ESTADO_CHECKLIST[reg.id_estado] ?? 'Desconocido';
 
  return (
    <div className="bt-card">
      <div className="bt-card-top">
        <div className="bt-card-top-left">
          <span className="bt-pill conductor">Conductor</span>
          <span className="bt-cod">{reg.cod_checklist}</span>
          <span className={`bt-estado bt-estado--${estado.toLowerCase()}`}>{estado}</span>
        </div>
        <span className="bt-time">{formatFechaHora(reg.creado_en)}</span>
      </div>
 
      <div className="bt-divider" />
 
      <div className="bt-card-body">
        <div className="bt-grid">
          <div className="bt-field">
            <span className="bt-label">Conductor</span>
            <div className="bt-mechs">
              <div className="bt-avatar bt-avatar--conductor">{iniciales(reg.conductor)}</div>
              <span className="bt-mech-name">{reg.conductor}</span>
            </div>
          </div>
          <div className="bt-field">
            <span className="bt-label">Kilometraje Registrado</span>
            <span className="bt-value">{reg.km_actual?.toLocaleString('es-CL')} km</span>
          </div>
        </div>
 
        <div className="bt-field">
          <span className="bt-label">Evaluación de Partes</span>
          <div className="bt-counters">
            <div className="bt-counter bt-counter--bueno">
              <div className="bt-counter-dot" />
              {reg.cant_bueno} Buenas
            </div>
            <div className="bt-counter bt-counter--revisar">
              <div className="bt-counter-dot" />
              {reg.cant_revisar} Revisar
            </div>
            <div className="bt-counter bt-counter--falta">
              <div className="bt-counter-dot" />
              {reg.cant_faltas} Faltan
            </div>
          </div>
        </div>
 
        {reg.observaciones && (
          <div className="bt-field bt-field--full">
            <span className="bt-label">Observaciones del conductor</span>
            <span className="bt-value bt-value--muted">{reg.observaciones}</span>
          </div>
        )}
      </div>
 
      <FooterDoc onVerDoc={onVerDoc} cargando={cargando} />
    </div>
  );
}

function FooterDoc({ onVerDoc, cargando, docDisponible = true }) {
  return (
    <div className="bt-card-footer">
      <button
        className="bt-doc-btn"
        onClick={onVerDoc}
        disabled={cargando || !docDisponible}
        title={!docDisponible ? 'El documento estará disponible cuando el vehículo sea entregado' : undefined}
      >
        {cargando
          ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 12 }} /> Abriendo...</>
          : <><i className="pi pi-file-pdf" style={{ fontSize: 12 }} /> Ver documento</>
        }
      </button>
      <span className="bt-doc-hint">
        {docDisponible ? (
          <>
            <i className="pi pi-external-link" style={{ fontSize: 10 }} />
            Nueva pestaña
          </>
        ) : (
          <>
            <i className="pi pi-lock" style={{ fontSize: 10 }} />
            Disponible al entregar
          </>
        )}
      </span>
    </div>
  );
}